import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { AuthShell, authButton, authInput, authLabel } from '../../components/Auth/AuthShell';
import { useAuth } from '../../contexts/AuthContext';

/** Where the emailed reset link lands: Supabase puts a one-time access token in the
 * URL fragment (or an error if the link expired). */
function readLinkToken(): { token: string | null; failed: boolean } {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return { token: params.get('access_token'), failed: params.has('error') || params.has('error_code') };
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const [{ token, failed }] = useState(readLinkToken);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Keep the token out of browser history and anything that reads the address bar.
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
  }, []);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) return setError(t('auth.passwordsMismatch'));
    if (password.length < 6) return setError(t('auth.passwordTooShort'));
    setLoading(true);
    try {
      const response = await resetPassword({ access_token: token ?? undefined, password });
      toast.success(t('auth.reset.done'));
      navigate(response.user.onboarding_completed ? '/dashboard' : '/onboarding');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.reset.invalid'));
    } finally {
      setLoading(false);
    }
  };

  if (!token || failed) {
    return (
      <AuthShell title={t('auth.reset.linkTitle')}>
        <p className="mb-6 text-sm text-text-secondary [text-wrap:pretty]">{t('auth.reset.linkInvalid')}</p>
        <Link to="/forgot-password" className={`${authButton} block text-center`}>{t('auth.reset.requestNew')}</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t('auth.reset.linkTitle')}
      footer={<Link to="/login" className="text-brand-primary font-medium hover:underline">{t('auth.reset.backToLogin')}</Link>}
    >
      <form onSubmit={save}>
        <div className="mb-4">
          <label htmlFor="link-password" className={authLabel}>{t('auth.reset.newPassword')}</label>
          <input id="link-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className={authInput} required />
        </div>
        <div className="mb-6">
          <label htmlFor="link-confirm" className={authLabel}>{t('auth.reset.confirmNew')}</label>
          <input id="link-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={authInput} required />
        </div>
        {error && <div role="alert" className="mb-4 p-3 bg-status-danger/10 text-status-danger text-sm rounded-lg">{error}</div>}
        <button type="submit" disabled={loading} className={authButton}>{loading ? t('auth.reset.saving') : t('auth.reset.save')}</button>
      </form>
    </AuthShell>
  );
}
